import type { CountyOption, StateOption } from '../types';

/**
 * Client-side filter for ComboBox items that have a label property.
 * @param {object} menu - ComboBox filter callback argument
 * @param {object} menu.item - The item to check
 * @param {string} menu.inputValue - Current text in the ComboBox input
 * @return {boolean} Whether the item should be shown
 */
export const filterByLabel = ({
  item,
  inputValue,
}: {
  item: { label: string };
  inputValue: string | null;
}) => {
  if (!inputValue) return true;
  return item.label.toLowerCase().includes(inputValue.toLowerCase());
};

/**
 * Client-side filter for State ComboBox items.
 * Matches on state name or abbreviation. Passes through when input matches selected item.
 * @param {object} menu - ComboBox filter callback argument
 * @param {StateOption | null} selectedState - Currently selected state for passthrough check
 * @return {boolean} Whether the item should be shown
 */
export const filterState = (
  { item, inputValue }: { item: StateOption; inputValue: string | null },
  selectedState: StateOption | null
) => {
  if (!inputValue) return true;
  if (
    selectedState &&
    inputValue === `${selectedState.name} (${selectedState.abbreviation})`
  )
    return true;
  const search = inputValue.toLowerCase();
  return (
    item.name.toLowerCase().includes(search) ||
    item.abbreviation.toLowerCase().includes(search)
  );
};

/**
 * Client-side filter for County ComboBox items.
 * Passes through when input matches selected item.
 * @param {object} menu - ComboBox filter callback argument
 * @param {CountyOption | null} selectedCounty - Currently selected county for passthrough check
 * @return {boolean} Whether the item should be shown
 */
export const filterCounty = (
  { item, inputValue }: { item: CountyOption; inputValue: string | null },
  selectedCounty: CountyOption | null
) => {
  if (!inputValue) return true;
  if (selectedCounty && inputValue === item.shortName) return true;
  return item.shortName
    .toLowerCase()
    .includes((inputValue ?? '').toLowerCase());
};
